import Banner from '@/components/Banner';
import ImageGallery from '@/components/ImageGallery';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import Timeline from '@/components/Timeline';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { groupInOrder } from '@/lib/utils';

interface Award {
  year: number | null;
  title: string;
  organization: string;
}

const awards: Award[] = [
  {
    year: null,
    title: 'Event Management Award',
    organization: 'Rotary Club of Tiruchirapalli Pinnacle',
  },
  { year: 2023, title: 'Singapen Viruthu', organization: 'Captain TV' },
  {
    year: 2021,
    title: 'Osai Awards',
    organization: 'Saathanai Penmanigal by Aasai TV Karur',
  },
  {
    year: 2020,
    title: 'Shri Rathna Viruthu',
    organization: 'Bharathi Yuvakendra Madurai',
  },
  {
    year: 2019,
    title: 'Dr. Abdulkalam Woman Achiever Award',
    organization: 'Vision 2020',
  },
  {
    year: 2018,
    title: 'Bharathiyar Viruthu',
    organization: 'Honourable Minister Ma Pa Pandiarajan',
  },
  { year: 2018, title: 'Teaching Excellence Award', organization: 'ISA Dubai' },
  {
    year: 2016,
    title: 'Achiever Award',
    organization: 'Director SP Muthuraman',
  },
  {
    year: 2016,
    title: 'Kalvi Chemmal Award',
    organization: 'Ulsga Thamizh Munneram Organization',
  },
  {
    year: 2016,
    title: 'Best Women Entrepreneur Award',
    organization: 'Thozhil Imayam Magazine',
  },
  {
    year: 2016,
    title: 'Best Women Achiever Award',
    organization: 'Puthuyugam Channel',
  },
  {
    year: 2015,
    title: 'Trichy No.1 Best Academy',
    organization: 'Thamizhar Kalai Peravai',
  },
  {
    year: 2015,
    title: 'Best Entrepreneur',
    organization: 'Business Thamizhagam',
  },
  { year: 2009, title: 'Platinum Achievement Award', organization: 'Amway' },
];

/*
 * One row per year rather than one per award.
 *
 * The list was previously a four-column grid that repeated the year above every
 * entry, so 2016's four awards printed "2016" four times and the sequence read
 * as unordered. Grouped, the years carry the section and it reads as the
 * chronology it is.
 *
 * Insertion order is otherwise preserved — the data is already newest-first —
 * with one exception: the undated award is moved to the end. It sits first in
 * the source, and an em dash above the most recent year read as a fault at the
 * top of a timeline.
 */
const byYear = groupInOrder(awards, (award) =>
  award.year === null ? 'undated' : String(award.year)
);

const timeline = [
  ...Array.from(byYear.entries()).filter(([year]) => year !== 'undated'),
  ...Array.from(byYear.entries()).filter(([year]) => year === 'undated'),
].map(([year, entries]) => ({
  marker: year === 'undated' ? '—' : year,
  entries: entries.map((award) => ({
    title: award.title,
    detail: award.organization,
  })),
}));

const Awards = ({ images }: { images: string[] }) => {
  const urls = images.map((image) => `/storage/${image}`);
  const galleryImages = urls.slice(4);

  return (
    <>
      <Banner
        image={urls[0]}
        alt="Rtn Thamizhachi Priya Govindaraj receiving an award"
        tagline="Countless achievements"
        title="Awards & Accolades"
        description="Rtn Thamizhachi Priya Govindaraj is a remarkable woman, celebrated with over 100 awards as both a successful female entrepreneur and an inspirational motivational speaker."
      >
        <ShareDialog
          url={window.location.href}
          title="Awards & Accolades - Samuthrika Academy"
          content="Rtn Thamizhachi Priya Govindaraj is a remarkable woman, celebrated with over 100 awards as both a successful Female Entrepreneur and an Inspirational Motivational Speaker."
        />
      </Banner>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <RevealGroup className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            <RevealItem className="flex flex-col gap-5">
              <Typography.Eyebrow>The roles she holds</Typography.Eyebrow>
              <Typography.Body className="text-muted-foreground">
                As the Pink Ambassador for India Turns Pink Trichy, she
                passionately advocates for a cause dear to her heart, deepening
                her role as a leader. Leading the{' '}
                <span className="font-semibold text-primary">
                  All Ladies League Trichy Chapter as Chairperson
                </span>
                , an esteemed international forum, she guides with grace and
                conviction, embodying the strength of empowered women.
              </Typography.Body>
              <Typography.Body className="text-muted-foreground">
                As the{' '}
                <span className="font-semibold text-primary">
                  Executive Director of Bizet Trichy
                </span>
                , a dynamic business platform, she sets a shining example of
                leadership, fostering an atmosphere of growth and collaboration.
                Her title as{' '}
                <span className="font-semibold text-primary">
                  Mrs. Congeniality Tamilnadu 2021
                </span>
                , among the top 10 contestants, reflects her innate grace and
                charm.
              </Typography.Body>
            </RevealItem>

            {urls[1] && (
              <RevealItem>
                <div className="aspect-4/3 overflow-hidden rounded-xl bg-muted shadow-card">
                  <img
                    className="size-full object-cover"
                    src={urls[1]}
                    alt=""
                    loading="lazy"
                  />
                </div>
              </RevealItem>
            )}
          </RevealGroup>

          {/* The pair below previously overlapped via md:-ml-10, which cropped
              the left image at every width the negative margin was not tuned
              for. A plain two-up holds at all of them. */}
          <RevealGroup className="grid gap-4 md:grid-cols-2">
            {[urls[2], urls[3]].filter(Boolean).map((url) => (
              <RevealItem key={url}>
                <div className="aspect-4/3 overflow-hidden rounded-xl bg-muted shadow-card">
                  <img
                    className="size-full object-cover"
                    src={url}
                    alt=""
                    loading="lazy"
                  />
                </div>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal className="flex flex-col gap-4">
            <Typography.Body className="max-w-prose text-muted-foreground">
              Beyond the limelight, she transforms into a passionate{' '}
              <span className="font-semibold text-primary">
                Pattimandram speaker
              </span>
              , weaving words infused with passion and purpose. Her influence
              extends to various openings and launches, where she graces the
              occasion as a chief guest.
            </Typography.Body>
            <Typography.Body className="max-w-prose text-muted-foreground">
              Among her many accolades, the{' '}
              <span className="font-semibold text-primary">
                Best Entrepreneur Director of the Year 2018, from ISA Dubai
              </span>{' '}
              shines as a beacon of her business acumen.
            </Typography.Body>
          </Reveal>
        </SectionLayout>
      </Band>

      <Band tone="surface" pattern="stitch" seam>
        <SectionLayout space="lg">
          <Reveal className="flex max-w-2xl flex-col gap-3">
            <Typography.Eyebrow>The record</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              Fifteen years of honours
            </Typography.Display2>
            <Typography.Body className="text-muted-foreground">
              Each award marking a milestone in an illustrious journey.
            </Typography.Body>
          </Reveal>

          <Timeline groups={timeline} unit={['award', 'awards']} />
        </SectionLayout>
      </Band>

      {galleryImages.length > 0 && (
        <Band tone="raised">
          <SectionLayout space="lg">
            <Reveal className="flex flex-col gap-3">
              <Typography.Eyebrow>On the night</Typography.Eyebrow>
              <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                Awards, in pictures
              </Typography.Display2>
            </Reveal>

            <ImageGallery
              images={galleryImages}
              alt="Photographs from award ceremonies"
            />

            <Reveal>
              <Typography.Body className="max-w-prose text-muted-foreground">
                Rtn Thamizhachi Priya Govindaraj&apos;s journey transcends
                personal triumph; it&apos;s a saga that inspires, empowers, and
                celebrates the limitless potential within each individual.
              </Typography.Body>
            </Reveal>
          </SectionLayout>
        </Band>
      )}
    </>
  );
};

export default Awards;
